import { useEffect, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import FilterPanel, { OptionList, type FilterOption, type FilterSection } from './FilterPanel';

/**
 * One filter, described rather than drawn, so the grid can lay it out as a
 * row in the phone filter panel (FilterPanel) and as a removable pill.
 *
 * `id` must be unique within the grid. `label` is the name in the panel's
 * left column and on the pill.
 */
export type MobileFilterField = {
  id: string;
  label: string;
  /** One line under the heading on the right. */
  hint?: string;
} & (
  | {
      /** Pick one. The option equal to `defaultValue` is "no filter". */
      kind: 'select';
      value: string;
      defaultValue: string;
      options: FilterOption[];
      onChange: (value: string) => void;
    }
  | {
      /** Pick any; none picked is "no filter". */
      kind: 'multi';
      value: string[];
      options: FilterOption[];
      /** First row, ticked when nothing is picked. */
      allLabel: string;
      onChange: (value: string[]) => void;
    }
  | {
      /** Free text; '' is "no filter". */
      kind: 'text';
      value: string;
      placeholder?: string;
      inputType?: 'text' | 'number';
      onChange: (value: string) => void;
    }
  | {
      /** From / to dates (yyyy-mm-dd); both '' is "no filter". */
      kind: 'dateRange';
      from: string;
      to: string;
      onFromChange: (value: string) => void;
      onToChange: (value: string) => void;
    }
  | {
      /** On / off, shown as two rows; off is "no filter". */
      kind: 'toggle';
      checked: boolean;
      offLabel: string;
      onLabel: string;
      onChange: (checked: boolean) => void;
    }
  | {
      /** Anything else: the page's own control, drawn as-is in the panel. */
      kind: 'custom';
      content: ReactNode;
      /** What is picked, for the left column and the pill; unset = no filter. */
      summary?: string;
      /** Undo it - the pill's ×. Without it the pill has no ×. */
      onClear?: () => void;
    }
);

export interface MobileFiltersConfig {
  fields: MobileFilterField[];
  /**
   * For a page whose filters are a draft until Apply: apply the draft. The
   * panel's "Show results" calls it; a pill's × calls it after resetting
   * that one field. Omit when every change already applies on its own.
   */
  onApply?: () => void;
  /** Draft pages: true when the draft differs from what the grid shows. */
  dirty?: boolean;
  /**
   * Draft pages: put the draft back to what the grid shows. Called when the
   * panel opens and when it closes without applying, so the fields (and the
   * pills drawn from them) always match the grid while it is shut.
   */
  onReset?: () => void;
  /** Clear all filters: everything to its default, applied now. */
  onClear: () => void;
  /**
   * Controls from the page's filter row that are not filters (an Export
   * button, a toggle that changes the view): on a phone they stay in the
   * toolbar, under the search, since the filter row itself is not drawn.
   */
  extras?: ReactNode;
}

const fmtDate = (d: string) => d || '…';

/** What a field has picked, as short text; undefined when it is at its default. */
export const fieldSummary = (f: MobileFilterField): string | undefined => {
  switch (f.kind) {
    case 'select':
      return f.value === f.defaultValue ? undefined : f.options.find((o) => o.value === f.value)?.label ?? f.value;
    case 'multi': {
      if (!f.value.length) return undefined;
      const names = f.value.map((v) => f.options.find((o) => o.value === v)?.label ?? v);
      return names.length <= 2 ? names.join(', ') : `${names.length} selected`;
    }
    case 'text':
      return f.value.trim() ? f.value.trim() : undefined;
    case 'dateRange':
      return f.from || f.to ? `${fmtDate(f.from)} – ${fmtDate(f.to)}` : undefined;
    case 'toggle':
      return f.checked ? f.onLabel : undefined;
    case 'custom':
      return f.summary || undefined;
  }
};

/** Set a field back to its default. False when it cannot be (custom without onClear). */
const resetField = (f: MobileFilterField): boolean => {
  switch (f.kind) {
    case 'select':
      f.onChange(f.defaultValue);
      return true;
    case 'multi':
      f.onChange([]);
      return true;
    case 'text':
      f.onChange('');
      return true;
    case 'dateRange':
      f.onFromChange('');
      f.onToChange('');
      return true;
    case 'toggle':
      f.onChange(false);
      return true;
    case 'custom':
      if (!f.onClear) return false;
      f.onClear();
      return true;
  }
};

const inputClass = (active: boolean) =>
  `h-10 w-full rounded-lg border px-3 text-sm outline-none transition-colors placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15 ${
    active ? 'border-indigo-300 bg-indigo-50/60 font-semibold text-indigo-900' : 'border-slate-200 bg-white text-slate-700'
  }`;

/**
 * The phone filter UI for a DataGrid: the icon beside the search (with a
 * count), the panel it opens, and the pills of what is applied. DataGrid
 * calls this itself when given `mobileFilters`; pages only describe fields.
 */
export const useMobileFilters = (config: MobileFiltersConfig | undefined) => {
  const [open, setOpen] = useState(false);
  // A pill's × on a draft page: reset the field, then apply on the next
  // render, once the draft holds the reset value.
  const [pendingApply, setPendingApply] = useState(false);
  useEffect(() => {
    if (!pendingApply) return;
    setPendingApply(false);
    config?.onApply?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingApply]);

  if (!config || config.fields.length === 0) return null;

  const { fields, onApply, dirty, onReset, onClear, extras } = config;
  const active = fields.filter((f) => fieldSummary(f) !== undefined);

  const onOpenChange = (next: boolean) => {
    // Opening, or closing without applying: the draft goes back to the grid's.
    onReset?.();
    setOpen(next);
  };
  const showResults = () => {
    if (onApply && dirty !== false) onApply();
    setOpen(false);
  };
  const clearAll = () => {
    onClear();
    setOpen(false);
  };
  const removeOne = (f: MobileFilterField) => {
    if (resetField(f) && onApply) setPendingApply(true);
  };
  const actionLabel = onApply && dirty ? 'Show results' : 'Done';

  const renderField = (f: MobileFilterField): ReactNode => {
    switch (f.kind) {
      case 'select':
        return <OptionList options={f.options} value={f.value} onChange={f.onChange} />;
      case 'multi':
        return <OptionList multiple allLabel={f.allLabel} options={f.options} value={f.value} onChange={f.onChange} />;
      case 'text':
        return (
          <input
            type={f.inputType ?? 'text'}
            value={f.value}
            onChange={(e) => f.onChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && showResults()}
            placeholder={f.placeholder}
            className={inputClass(!!f.value.trim())}
          />
        );
      case 'dateRange':
        return (
          <div className="grid grid-cols-1 gap-3">
            {(
              [
                ['From', f.from, f.onFromChange],
                ['To', f.to, f.onToChange],
              ] as const
            ).map(([label, value, onChange]) => (
              <label key={label} className="block">
                <span className="mb-1.5 block text-xs font-semibold text-slate-600">{label}</span>
                <input type="date" value={value} onChange={(e) => onChange(e.target.value)} className={inputClass(!!value)} />
              </label>
            ))}
          </div>
        );
      case 'toggle':
        return (
          <OptionList
            options={[
              { value: '', label: f.offLabel },
              { value: 'on', label: f.onLabel },
            ]}
            value={f.checked ? 'on' : ''}
            onChange={(v) => f.onChange(v === 'on')}
          />
        );
      case 'custom':
        return f.content;
    }
  };

  const sections: FilterSection[] = [...fields]
    .sort((a, b) => a.label.localeCompare(b.label)) // A-Z down the left
    .map((f) => ({ id: f.id, label: f.label, hint: f.hint, summary: fieldSummary(f), content: renderField(f) }));

  const footer = (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={clearAll}
        disabled={active.length === 0}
        title="Remove every filter from the grid now"
        className="flex h-10 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:cursor-default disabled:border-slate-100 disabled:text-slate-300 disabled:hover:bg-white"
      >
        <X size={15} /> Clear all filters
      </button>
      <button
        type="button"
        onClick={showResults}
        className="ml-auto h-10 flex-1 rounded-lg bg-indigo-600 px-6 text-sm font-bold text-white transition-colors hover:bg-indigo-700"
      >
        {actionLabel}
      </button>
    </div>
  );

  const button = (
    <FilterPanel
      open={open}
      onOpenChange={onOpenChange}
      activeCount={active.length}
      sections={sections}
      footer={footer}
      headerAction={
        <button
          type="button"
          onClick={showResults}
          className="h-8 rounded-lg bg-indigo-600 px-3.5 text-xs font-bold text-white transition-colors hover:bg-indigo-700"
        >
          {onApply && dirty ? 'Apply' : 'Done'}
        </button>
      }
    />
  );

  /** What is applied, as pills; a row that scrolls sideways. */
  const pills = active.length > 0 && (
    <div className="-mx-1 flex w-full items-center gap-1.5 overflow-x-auto px-1 pb-0.5">
      {active.map((f) => {
        const text = fieldSummary(f);
        const removable = f.kind !== 'custom' || !!f.onClear;
        return (
          <span
            key={f.id}
            title={`${f.label}: ${text}`}
            className={`flex h-7 shrink-0 items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 pl-2.5 text-xs text-indigo-800 ${
              removable ? 'pr-1' : 'pr-2.5'
            }`}
          >
            <span className="text-indigo-500">{f.label}:</span>
            <span className="max-w-[160px] truncate font-semibold">{text}</span>
            {removable && (
              <button
                type="button"
                onClick={() => removeOne(f)}
                aria-label={`Remove ${f.label} filter`}
                className="flex h-5 w-5 items-center justify-center rounded-full text-indigo-400 transition-colors hover:bg-indigo-100 hover:text-indigo-700"
              >
                <X size={12} />
              </button>
            )}
          </span>
        );
      })}
      <button
        type="button"
        onClick={onClear}
        className="h-7 shrink-0 rounded-full px-2.5 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
      >
        Clear all
      </button>
    </div>
  );

  return {
    button,
    pills:
      pills || extras ? (
        <>
          {pills}
          {extras && <div className="flex w-full flex-wrap items-center gap-2">{extras}</div>}
        </>
      ) : null,
  };
};

/**
 * The same phone filter UI for a page that draws its own table and toolbar
 * (no DataGrid): the page's search (optional) with the filter icon beside it,
 * then the pills. Render it in place of the page's filter row on a phone:
 *
 *   const phone = usePhone();
 *   ...
 *   {phone ? <MobileFilterBar config={mobileFilters} search={searchInput} /> : desktopFilterRow}
 */
export const MobileFilterBar = ({
  config,
  search,
  className = '',
}: {
  config: MobileFiltersConfig;
  /** The page's search input, kept beside the icon. */
  search?: ReactNode;
  className?: string;
}) => {
  const mobile = useMobileFilters(config);
  if (!mobile) return search ? <div className={className}>{search}</div> : null;
  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2">
        {search && <div className="min-w-0 flex-1">{search}</div>}
        {mobile.button}
      </div>
      {mobile.pills}
    </div>
  );
};
